import flyd from '../../vendor/flyd';
import { compose, curry, filter, forEach, map } from 'ramda';

function mapStream(el, eventName, fn) {
  let stream = flyd.stream();
  el.addEventListener(eventName, stream);
  return fn ? flyd.map(fn, stream) : stream;
};

function transduceStream(el, eventName, fn) {
  let stream = flyd.stream();
  el.addEventListener(eventName, stream);
  return flyd.transduce(fn, stream);
};

function isSingleTouch(e) { return e.touches && e.touches.length === 1; };

function isDoubleTouch(e) { return e.touches && e.touches.length === 2; };

function addMomentum(toStream, endStream) {
  let momentumStream = flyd.stream()(0);

  let result = flyd.combine(function (to, momentum) {
    return to.end() ? momentum() : to();
  }, [toStream, momentumStream]);

  let decay = 1;

  flyd.on(function () {
    const timer = every(1000 / 60);
    const momentumStart = new Date();
    const momentumDecay = 0.2;
    if (!toStream()) {
      return momentumStream.end(true);
    }

    const { deltaX, deltaY } = toStream();

    flyd.map(function (i) {
      if (decay <= 0.005) {
        momentumStream.end(true);
        timer.end(true);
      } else {
        decay = Math.exp(-(1 - momentumDecay) * 10 * (Date.now() - momentumStart) / 1000);
        //console.log('decay: ', decay, deltaX, deltaY);
        momentumStream({ deltaX: deltaX * decay, deltaY: deltaY * decay });
      }
    }, timer);
  }, toStream.end);

  return result;
};

export function initManipulators(store, translator, el) {
  let playerEl = el.parentNode.parentNode;
  let offset = { x: 0, y: 0 };
  let rect = { width: 0, height: 0 };
  let commands = [];
  let hasEvents = {};

  let dragging = false;
  let lastMousedown = false;

  function resize(newRect) {
    const playerRect = playerEl.getBoundingClientRect();
    offset = {
      x: playerRect.left,
      y: playerRect.top,
    };
    rect = newRect;
  }

  function touchLastMove(ev) {
    if (dragging) dragging.lastMove = new Date();
    return ev;
  }

  function callTool(which, options={}) {
    return function (ev) {
      if (!hasEvents[which]) return;

      var command, tool, result;

      for (var i = commands.length - 1; i >= 0; i--) {
        command = commands[i];
        tool = command.tool;
        if (!command.enabled || !tool[which]) continue;

        result = tool[which].call(command, ev);

        if (options.findFirst) {
          if (result !== false) break;
        } else if (!options.doAll) {
          if (result === false) break;
        }
      }

      return result;
    };
  };

  function toCoords(ev) {
    // Calling preventDefault on 'mousedown' prevents the 'mouseup' event from firing when inside
    // an iframe. No reason to call preventDefault on these events, so don't.

    return {
      rect,
      clientX: ev.clientX - offset.x,
      clientY: ev.clientY - offset.y,
      deltaX: ev.movementX,
      deltaY: ev.movementY,
      originalEvent: ev,
      which: ev.which,
      altKey: ev.altKey,
      ctrlKey: ev.ctrlKey,
      metaKey: ev.metaKey,
      shiftKey: ev.shiftKey,
    };
  };

  function toMouseEvent(e) {
    if (!e.touches || !e.touches.length) return e;
    e.preventDefault();
    const elRect = e.srcElement.getBoundingClientRect();
    return {
      which: 1,
      rect: rect,
      clientX: e.touches[0].clientX - elRect.left,
      clientY: e.touches[0].clientY - elRect.top,
      originalEvent: e,
    };
  };

  flyd.on(callTool('dragenter'), mapStream(el, 'dragenter'));
  flyd.on(callTool('dragover'), mapStream(el, 'dragover'));
  flyd.on(callTool('dragleave'), mapStream(el, 'dragleave'));
  flyd.on(callTool('dblclick'), mapStream(el, 'dblclick'));

  const keysdown = {};
  flyd.on((kd) => {
    if (!keysdown[kd.key]) {
      callTool('keydown')(kd);
      keysdown[kd.key] = setInterval(() => callTool('keyheld')(kd), 17);
    }
  }, mapStream(el, 'keydown'));

  flyd.on((ku) => {
    if (keysdown[ku.key]) {
      clearInterval(keysdown[ku.key]);
      delete keysdown[ku.key];
    }

    callTool('keyup')(ku);
  }, mapStream(el, 'keyup'));

  let clickStream = mapStream(el, 'click', toCoords);
  let mousedownStream = mapStream(el, 'mousedown', toCoords);
  let mousemoveStream = mapStream(el, 'mousemove', toCoords);
  let mouseupStream = mapStream(window, 'mouseup', toCoords);
  let moveStream = flyd.stream();
  let dragStream = flyd.stream();

  let touchstartStream = transduceStream(el, 'touchstart', compose(filter(isSingleTouch), map(toMouseEvent)));
  let touchendStream = mapStream(el, 'touchend', toMouseEvent);

  flyd.on((md) => {
    md.originalEvent.target.focus();
    callTool('mousedown')(md);
    lastMousedown = { start: new Date(), event: md };

    let tool = callTool('drag', { findFirst: true })(md);
    if (!tool) return;

    dragging = { lastMove: new Date() };

    let clientX = null;
    let clientY = null;

    function toDeltaCoords(ev) {
      ev.preventDefault();
      let newEv = {
        rect,
        clientX: ev.clientX - offset.x,
        clientY: ev.clientY - offset.y,
        originalEvent: ev,
        which: ev.which,
        altKey: ev.altKey,
        ctrlKey: ev.ctrlKey,
        metaKey: ev.metaKey,
        shiftKey: ev.shiftKey,
      };
      if (ev.movementX === undefined) {
        newEv.deltaX = clientX === null ? 0 : ev.clientX - clientX;
        newEv.deltaY = clientX === null ? 0 : ev.clientY - clientY;
      } else {
        newEv.deltaX = ev.movementX;
        newEv.deltaY = ev.movementY;
      }
      clientX = ev.clientX;
      clientY = ev.clientY;
      return newEv;
    }

    moveStream = transduceStream(window, 'mousemove', compose(
      map(touchLastMove), map(toDeltaCoords)
    ));

    dragStream.end(true);
    dragStream = tool.momentum ? addMomentum(moveStream, mouseupStream) : moveStream;
    dragStream.map(tool.handle);
  }, mousedownStream);

  flyd.on((ts) => {
    callTool('mousedown')(ts);
    lastMousedown = { start: new Date(), event: ts };

    let tool = callTool('drag', { findFirst: true })(ts);
    if (!tool) return;

    dragging = { lastMove: new Date() };

    let { clientX, clientY } = ts;

    function toDelta(e) {
      const newE = Object.assign(e, {
        deltaX: e.clientX - clientX,
        deltaY: e.clientY - clientY,
      });
      clientX = e.clientX;
      clientY = e.clientY;
      return newE;
    };

    moveStream = transduceStream(el, 'touchmove', compose(
      filter(isSingleTouch), map(touchLastMove), map(toMouseEvent), map(toDelta)
    ));

    dragStream.end(true);
    dragStream = tool.momentum ? addMomentum(moveStream, touchendStream) : moveStream;
    dragStream.map(tool.handle);
  }, touchstartStream);

  let endingStream = flyd.merge(touchendStream, mouseupStream);

  flyd.on((mu) => {
    if (dragging) {
      moveStream.end(true);
      // End momentum if last mousemove event is gt 200ms from mouseup.
      if (new Date() - dragging.lastMove > 200) {
        dragStream.end(true);
      }
    }

    //console.log('mu', new Date() - lastMousedown.start, lastMousedown.event, mu, muX, muY);

    callTool('mouseup')(mu);

    if (lastMousedown && new Date() - lastMousedown.start < 300) {
      const muX = Math.abs(mu.clientX - lastMousedown.event.clientX);
      const muY = Math.abs(mu.clientY - lastMousedown.event.clientY);

      if (isNaN(muX)) {
        callTool('click')(lastMousedown.event);
      } else if (muX <= 5 && muY <= 5) {
        callTool('click')(mu);
      }
    }

    dragging = false;
    lastMousedown = false;
  }, endingStream);

  flyd.on(callTool('hover'), flyd.transduce(filter(() => !dragging && hasEvents['hover']), mousemoveStream));

  //flyd.map((scroll) => {
  //  console.log('scroll?', scroll);
  const scrollHandler = callTool('scroll');
  mapStream(el, 'wheel', (ev) => {
    if (hasEvents['scroll']) {
      ev.preventDefault();
      scrollHandler({
        delta: -ev.deltaY / (ev.wheelDelta ? 200 : 10),
        originalEvent: ev,
      });
    }
  });

  function extractTouches(e) {
    e.preventDefault();
    return {
      a: { x: e.touches[0].clientX, y: e.touches[0].clientY },
      b: { x: e.touches[1].clientX, y: e.touches[1].clientY },
    };
  };

  let zoom = flatMap((ts) => {
    let f = ts;
    let moveStream = transduceStream(el, 'touchmove', compose(
      filter(isDoubleTouch), map(extractTouches), map(function (g) {
        var result = {
          pinchX: (g.b.x - f.b.x) - (g.a.x - f.a.x),
          pinchY: (f.a.y - g.a.y) - (f.b.y - g.b.y),
          dragX: ((g.a.x - f.a.x) + (g.b.x - f.b.x)) / 2,
          dragY: ((g.a.y - f.a.y) + (g.b.y - f.b.y)) / 2,
          a: g.a, b: g.b,
        };
        f = g;
        return result;
      })
    ));
    flyd.endsOn(touchendStream, moveStream);
    return moveStream;
  }, transduceStream(el, 'touchstart', compose(

    filter(isDoubleTouch), map(extractTouches)
  )));

  flyd.on((z) => {
    const { pinchX, pinchY, dragX, dragY, a, b } = z;
    if ((Math.abs(pinchX) + Math.abs(pinchY)) > (Math.abs(dragX) + Math.abs(dragY))) {
      // pinch
      callTool('pinch')({
        deltaX: (a.x > b.x ? -pinchX : pinchX),
        deltaY: (a.y > b.y ? pinchY : -pinchY),
      });
    } else {
      // swipe
      callTool('swipe')({ deltaX: dragX, deltaY: dragY });
    }
  }, zoom);

  return {
    resize,

    updateTools: (newCommands) => {
      commands = newCommands;
      hasEvents = {};

      forEach(function (command) {
        Object.keys(command.tool).forEach((k) => hasEvents[k] = true);
      }, commands);
    },
  };
}

function every(dur) {
  var s = flyd.stream();
  var target = Date.now();
  function timer() {
    if (s.end()) return;
    var now = Date.now();
    target += dur;
    s(now);
    setTimeout(timer, target - now);
  }

  timer();
  return s;
};

function flatMap(f, s) {
  return flyd.combine(function (s, own) {
    flyd.map(own, f(s()));
  }, [s]);
};



// WEBPACK FOOTER //
// ./src/translators/manipulators.js